import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../services/api";

const PROVIDER_LABELS = {
  google: "Google",
  microsoft: "Microsoft",
};

export default function OAuthCallback() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = searchParams.get("token");
    const refreshToken = searchParams.get("refresh_token");
    const provider = searchParams.get("provider");
    const err = searchParams.get("error");

    if (err) {
      setError(mapError(err, provider, t));
      return;
    }

    if (!token) {
      setError(t("miscPages.oauth.noToken"));
      return;
    }

    // Store tokens
    localStorage.setItem("access_token", token);
    if (refreshToken) localStorage.setItem("refresh_token", refreshToken);

    // Fetch user profile with the new token
    api.get("v1/users/me/")
      .then((r) => {
        const userData = r.data;
        localStorage.setItem("user_data", JSON.stringify(userData));

        const roles = userData.roles || [];
        const isEmployeeOnly =
          roles.includes("EMPLOYE") &&
          !roles.some((role) => ["ADMIN", "RH", "SUPERVISOR", "COMPTABLE"].includes(role));

        window.location.replace(isEmployeeOnly ? "/portal/dashboard" : "/dashboard");
      })
      .catch(() => {
        window.location.replace("/dashboard");
      });
  }, [searchParams, navigate, t]);

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: "var(--lm-canvas)" }}
      >
        <div className="max-w-sm w-full text-center space-y-4">
          <div
            className="rounded-xl p-6"
            style={{ backgroundColor: "var(--lm-s1)", border: "1px solid rgba(239,68,68,0.3)" }}
          >
            <p className="text-sm font-medium mb-1" style={{ color: "#ef4444" }}>
              {t("miscPages.oauth.failed")}
            </p>
            <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>{error}</p>
          </div>
          <button
            onClick={() => window.location.replace("/login")}
            className="text-sm font-medium"
            style={{ color: "#5e6ad2" }}
          >
            {t("miscPages.oauth.back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: "var(--lm-canvas)" }}
    >
      <div className="text-center space-y-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: "#5e6ad2" }} />
        <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>{t("miscPages.oauth.connecting")}</p>
      </div>
    </div>
  );
}

function mapError(code, provider, t) {
  const label = PROVIDER_LABELS[provider] || "SSO";
  const messages = {
    missing_code: t("miscPages.oauth.errMissingCode", { label }),
    invalid_state: t("miscPages.oauth.errInvalidState"),
    token_exchange_failed: t("miscPages.oauth.errTokenExchange", { label }),
    missing_email: t("miscPages.oauth.errMissingEmail", { label }),
    server_error: t("miscPages.oauth.errServer"),
    access_denied: t("miscPages.oauth.errAccessDenied", { label }),
  };
  return messages[code] || t("miscPages.oauth.errUnknown", { code });
}
